import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NgForm, NgModel } from '@angular/forms';
import { Address } from '../../models/address-model';
import { CountryEnumerationItem, EnumsService } from '../../services/enums.service';
import { EnumerationItem } from 'projects/shared/models/enumeration-item.model';
import { v4 as uuidv4 } from 'uuid';
import { ApplicationContextService } from '../../services/application-context.service';
import { ConfigurableFieldHostComponent } from '../configurable-field-host.component';
import { ZipCodeService } from '../../services/zip-code.service';
import { ZipCodeLookupResult } from '../../models/zipcode-lookup-result.model';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { MakeProvider } from '../abstract-value-accessor';
import { formViewProvider } from '../../services/form-view.provider';
import * as _ from 'lodash';
import { NgxSpinnerService } from 'ngx-spinner';
import { AddressSelectedEvent } from 'projects/shared/address-autocomplete-v2/address-autocomplete-v2.component';

@Component({
    selector: 'address',
    templateUrl: 'address.component.html',
    styleUrls: ['./address.component.scss'],
    providers: [MakeProvider(AddressComponent)],
    viewProviders: [formViewProvider],
    standalone: false
})
export class AddressComponent extends ConfigurableFieldHostComponent implements OnInit {

  @ViewChild('addressForm') addressForm: NgForm | undefined;

  @ViewChild('stateOfAddress') stateElement: ElementRef | undefined;
  @ViewChild('zipCodeOfAddress') zipCodeElement: ElementRef | undefined;
  @ViewChild('cityOfAddress') cityElement: ElementRef | undefined;
  @ViewChild('control') zipCode: NgModel;
  @ViewChild('control') input: ElementRef<HTMLInputElement>;

  @Input()
  address: Address;

  @Input()
  canSetTbd: boolean = false;

  @Input()
  required: boolean = true;

  @Input()
  isDialogView: boolean = false;

  @Input()
  inEditMode: boolean = false;

  @Input()
  addressTbdLabel: string = 'TBD';

  @Input()
  showCountry: boolean = false;

  @Input()
  showCounty: boolean = false;

  @Input()
  requireUserToEnterZipCodeWhenTbd: boolean = false;

  @Input()
  fieldConfigSettings: AddressFieldConfigSettings = new AddressFieldConfigSettings();

  @Input()
  zipCodeLookupEnabled: boolean = false;

  @Input()
  set isTbd(value: boolean) {
    this._isTbd = value;
    this.onIsTbdChanged();
  }

  get isTbd(): boolean {
    return this._isTbd;
  }

  @Input()
  isTbdDisabled: boolean = false;

  @Input()
  set availableStates(availableStates: EnumerationItem[]) {
    if (availableStates && availableStates.length) {
      this.states = availableStates;
    } else {
      this.states = this._enumsService.states;
    }
  }

  @Output()
  addressTbdLabelChanged: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  addressChanged: EventEmitter<Address> = new EventEmitter<Address>();

  @Output()
  addressIsTbdChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  countryChanged: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  zipcodeSearchStarted: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  zipcodeSearchCompleted: EventEmitter<any> = new EventEmitter<any>();

  states: EnumerationItem[] = [];
  countries: CountryEnumerationItem[] = [];

  uniqueId: string;
  suggestions: ZipCodeLookupResult[] = [];
  loadingZipcodes: boolean = false;
  loadingCountries: boolean = false;
  placeHolder: string;
  mask: string;
  pattern: string;

  public isZipKnownByTheBorrower: boolean = false;

  private _isTbd: boolean = false;

  private _loadedWithRealAddress: boolean = false;

  get tbdLockedByExistingAddress(): boolean {
    return this._loadedWithRealAddress;
  }

  constructor(
    private readonly _enumsService: EnumsService,
    private readonly _ctxService: ApplicationContextService,
    private readonly _zipCodeService: ZipCodeService,
    private readonly _spinner: NgxSpinnerService,
    private readonly _cdr: ChangeDetectorRef
  ) {
    super();
    if (!this.address) {
      this.address = new Address();
    }
    this.uniqueId = uuidv4();
  }

  ngOnInit() {
    if (!this.states || !this.states.length) {
      this.states = this._enumsService.states;
    }
    if (
      this.canSetTbd &&
      this.address.address1?.toLocaleLowerCase() === 'tbd'
    ) {
      this._isTbd = true;
    }
    this._loadedWithRealAddress = this.hasRealAddress(this.address.address1);
    if (!this.address.state) {
      this.address.state = null;
    }

    if (this.showCountry) {
      this.loadingCountries = true;
      this._ctxService.context.subscribe(ctx => {
        this.countries = ctx.globalConfig.countries.map(c => {
          c.alpha2 = c.alpha2.toLowerCase();
          return c;
        });
        this.loadingCountries = false;
        this._cdr.detectChanges();
      });
    }

    if (this.address && !this.address.country) {
      this.address.country = "us";
    }

    if (this.requireUserToEnterZipCodeWhenTbd) {
      this.isZipKnownByTheBorrower = true;
    }

    this.placeHolder = 'XXXXX-XXXX';
    this.mask = '00000||00000-0000';
    this.pattern = '^[0-9]{5}(-[0-9]{4})?$'
  }

  onIsTbdChanged = () => {
    if (this.isTbd && this._loadedWithRealAddress) {
      this._isTbd = false;
    } else if (this.isTbd) {
      this.address.address1 = 'TBD';
      if (!this.isZipKnownByTheBorrower) {
        this.nullifyAddressWhenTbd();
      }
    } else {
      this.address.address1 = '';
    }
    this.addressIsTbdChanged.emit(this.isTbd);
  };

  private hasRealAddress = (address1?: string): boolean => {
    return !!address1
      && address1.trim().length > 0
      && address1.trim().toLocaleLowerCase() !== 'tbd';
  };

  onKeyDownOnAddress = (e: any) => {
    if (e.key === 'Tab') {
      this.doZipLookupOnBlur(e);
    }
  }

  onAddressBlurred = (e: any) => {
    if (e.relatedTarget?.id === 'search-zip-code-btn') {
      return;
    }
    this.doZipLookupOnBlur(e);
  }

  selectZipcode = (suggestion: ZipCodeLookupResult) => {
    this.sendSelectionChanges(suggestion);
  }

  onAddressTbdLabelChanged = () => {
    this.addressTbdLabelChanged.emit(this.addressTbdLabel);
  }

  onAnswerForZipcodeQuestionChanged = () => {
    if (!this.isZipKnownByTheBorrower) {
      this.nullifyAddressWhenTbd();
    }
  };

  setManuallyEnteredAddress(address: string) {
    this.address.address1 = address;
  }

  setGooglePickedAddress(address: Address) {
    this.address.address1 = "...";
    setTimeout(() => {
      this.address.address1 = address.address1;
      this.address.city = address.city;
      this.address.state = address.state;
      this.address.zipCode = address.zipCode;
      this.address.county = address.county;
    });
  }

  onAddressChanged = () => {
    this.addressChanged.emit({ ...this.address });
  }

  protected onAddressSelectedWithAutoComplete(e: AddressSelectedEvent) {
    this.address.address1 = e.streetAddress;
    this.address.city = e.city;
    this.address.state = e.state.toUpperCase();
    this.address.zipCode = e.zipCode;
    this.address.county = e.county;
    this.address.country = "us";
    this.onAddressChanged();
  }

  onCountySelected(e: { county: string; state: string }) {
    this.address.county = e.county;
    this.address.state = e.state;
    this.onAddressChanged();
  }

  searchZipcode = (selectionsDropdown: NgbDropdown) => {
    this.loadingZipcodes = true;
    this.suggestions = [];

    if (!this.address.zipCode) {
      this.loadingZipcodes = false;
      return;
    }

    this._spinner.show();
    this._zipCodeService.lookupZipCode(this.address.zipCode, true)
      .subscribe((suggestions) => {
        this.suggestions = suggestions;
        selectionsDropdown.open();
      }).add(() => {
        this.loadingZipcodes = false;
        this._spinner.hide();
        this._cdr.markForCheck();
      });
  }

  onCountryChanged = () => {
    if (this.address && this.address.country && this.address.country != "us") {
      this.address.address1 = "";
      this.address.city = "";
      this.address.state = "";
      this.address.zipCode = "";
      this.address.county = "";
    }

    this.countryChanged.emit(this.address.country);
  }

  validate = () => {
    this.addressForm?.form.markAllAsTouched();
    if (this.addressForm?.form.valid) {
      return true;
    }
    return false;
  };

  private doZipLookupOnBlur = (e: any) => {
    if (!this.address.zipCode || !this.zipCodeLookupEnabled) {
      return;
    }
    this.zipcodeSearchStarted.emit(e);
    this._spinner.show();
    this._zipCodeService.lookupZipCode(this.address.zipCode, true)
      .subscribe((suggestions) => {
        if (suggestions && suggestions.length > 0) {
          this.selectZipcode(suggestions[0]);
        }
        setTimeout(() => {
          this.zipcodeSearchCompleted.emit(e);
          this._spinner.hide();
          this._cdr.markForCheck();
        }, 250);
      }, err => {
        this._spinner.hide();
        this.zipcodeSearchCompleted.emit(e);
        this._cdr.markForCheck();
      });
  }

  private nullifyAddressWhenTbd = () => {
    this.address.zipCode = null;
    this.address.city = null;
    this.address.state = null;
    this.address.county = null;
  };

  private sendSelectionChanges = (suggestion: ZipCodeLookupResult) => {
    if (suggestion) {
      this.address.city = _.startCase(_.toLower(suggestion.city));
      this.address.state = suggestion.state.toUpperCase();
      this.address.zipCode = suggestion.zipcode;
      this.address.county = _.startCase(_.toLower(suggestion.county));
      this.addressChanged.emit({ ...this.address });
    }
  }
}

export class AddressFieldConfigSettings {
  stateField: string = 'state';
  addressField?: string = 'address';
  cityField?: string = 'city';
  zipCodeField?: string = 'zipCode';
  countyField?: string = 'county';
  countryField?: string = 'country';
}


